import { useCallback, useEffect, useState } from "react";

import FunctionalButton from "components/layout/FunctionalButton";
import { twMerge } from "tailwind-merge";
import { ReactComponent as FullscreenEnterIcon } from "assets/svgs/fullscreen_enter.svg";
import { ReactComponent as FullscreenExitIcon } from "assets/svgs/fullscreen_exit.svg";

interface FullscreenButtonProps {
    className?: string;
    iconClassName?: string;
}

const getFullscreenElement = () =>
    document.fullscreenElement ??
    (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ??
    null;

const requestFullscreen = async (el: HTMLElement) => {
    const w = el as HTMLElement & {
        webkitRequestFullscreen?: () => void;
        mozRequestFullScreen?: () => void;
        msRequestFullscreen?: () => void;
    };
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (w.webkitRequestFullscreen) w.webkitRequestFullscreen();
    else if (w.mozRequestFullScreen) w.mozRequestFullScreen();
    else if (w.msRequestFullscreen) w.msRequestFullscreen();
};

const exitFullscreen = async () => {
    const d = document as Document & {
        webkitExitFullscreen?: () => void;
        mozCancelFullScreen?: () => void;
        msExitFullscreen?: () => void;
    };
    if (document.exitFullscreen) await document.exitFullscreen();
    else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
    else if (d.mozCancelFullScreen) await d.mozCancelFullScreen();
    else if (d.msExitFullscreen) await d.msExitFullscreen();
};

const FullscreenButton = ({ className = "", iconClassName = "h-4 w-4" }: FullscreenButtonProps) => {
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const sync = () => setIsFullscreen(!!getFullscreenElement());
        sync();
        document.addEventListener("fullscreenchange", sync);
        document.addEventListener("webkitfullscreenchange", sync);
        return () => {
            document.removeEventListener("fullscreenchange", sync);
            document.removeEventListener("webkitfullscreenchange", sync);
        };
    }, []);

    const toggle = useCallback(async () => {
        try {
            if (getFullscreenElement()) await exitFullscreen();
            else await requestFullscreen(document.documentElement);
        } catch (e) {
            console.warn("Fullscreen request failed", e);
        }
    }, []);

    const iconCls = twMerge(iconClassName, "shrink-0");

    return (
        <FunctionalButton
            className={className}
            icon={
                isFullscreen ? (
                    <FullscreenExitIcon className={iconCls} />
                ) : (
                    <FullscreenEnterIcon className={iconCls} />
                )
            }
            hoverText={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            onClick={toggle}
        />
    );
};

export default FullscreenButton;
